import { TRACKING } from "../../core/components/LaunchDarklyProvider";
import { buildRouteArgs } from "../../pages/utils";
import { Page } from "../../resources/modelTypes";
import { SwipePanelRef } from "./online-invitation/mobile/SwipePanel";
import { useLDClient } from "launchdarkly-react-client-sdk";
import noop from "lodash/noop";
import React, {
  createContext,
  useCallback,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useDispatch } from "react-redux";
import { actions } from "redux-router5";

export type ResourceEditMetadata = {
  resourceType: "event" | "section";
  idOfResource: number;
};

export enum SidebarDisplayModes {
  OFF = "off",
  SM = "sm",
  LG = "lg",
}

export type SitePreviewContextType = {
  handleSidebarChange: (
    size: SidebarDisplayModes,
    showPreview?: boolean,
  ) => void;
  isMobile: boolean;
  onSectionEditClick: (
    idOfResource: number,
    resourceType: "event" | "section",
    page?: Page,
  ) => void;
  resourceEditMetadata: ResourceEditMetadata | null;
  setResourceEditMetadata: (metadata: ResourceEditMetadata | null) => void;
  showMobilePreview: boolean;
  sidebarDisplayMode: SidebarDisplayModes;
  swipePanelRef: React.RefObject<SwipePanelRef>;
};

const SitePreviewContext = createContext<SitePreviewContextType>({
  handleSidebarChange: () => noop,
  isMobile: false,
  onSectionEditClick: () => noop,
  resourceEditMetadata: null,
  setResourceEditMetadata: () => noop,
  showMobilePreview: false,
  sidebarDisplayMode: SidebarDisplayModes.SM,
  swipePanelRef: {
    current: null,
  },
});

type SitePreviewContextProviderProps = {
  children: ReactNode;
  isMobile: boolean;
};

export const SitePreviewContextProvider = ({
  children,
  isMobile,
}: SitePreviewContextProviderProps) => {
  const dispatch = useDispatch();
  const ldClient = useLDClient();
  const swipePanelRef = useRef<SwipePanelRef>(null);

  const [resourceEditMetadata, setResourceEditMetadata] =
    useState<ResourceEditMetadata | null>(null);

  const [showMobilePreview, setShowMobilePreview] = useState<boolean>(false);

  const [sidebarDisplayMode, setSidebarDisplayMode] =
    useState<SidebarDisplayModes>(SidebarDisplayModes.SM);

  const handleSidebarChange = useCallback(
    (size: SidebarDisplayModes, showPreview: boolean = false) => {
      setShowMobilePreview(showPreview);
      setSidebarDisplayMode(size);
    },
    [],
  );

  const onSectionEditClick = useCallback(
    (idOfResource: number, resourceType: "event" | "section", page?: Page) => {
      // Sets current resource being edited in sidebar
      setResourceEditMetadata({
        idOfResource,
        resourceType,
      });

      if (isMobile) {
        // On mobile, show the sidebar and hide mobile preview when editing
        handleSidebarChange(SidebarDisplayModes.SM);

        // Try and open swipe panel if it exists
        swipePanelRef.current?.open();
      }

      // Navigates to page and emits tracking event if clicking from site preview
      if (page) {
        const { name, params } = buildRouteArgs({
          data: page,
        });

        dispatch(actions.navigateTo(name, params));
      } else {
        ldClient?.track(TRACKING.ClickablePreview.OIpreviewSectionEditClicked);
      }
    },
    [dispatch, ldClient, handleSidebarChange, isMobile],
  );

  const contextValue: SitePreviewContextType = {
    handleSidebarChange,
    isMobile,
    onSectionEditClick,
    resourceEditMetadata,
    setResourceEditMetadata,
    showMobilePreview,
    sidebarDisplayMode,
    swipePanelRef,
  };

  return (
    <SitePreviewContext.Provider value={contextValue}>
      {children}
    </SitePreviewContext.Provider>
  );
};

export default SitePreviewContext;
